import { WorkflowType } from './Client.js';

/**
 * Toggleable autonomous workflow features. A client must opt in to each of these individually
 * (see `Client.enabledFeatures`), on top of the master `Client.enabled` killswitch.
 *
 * These are the *outward-facing, self-acting* behaviors (sending mail, assigning FEs, writing
 * OTRS). Ticket intake (ingestion/update) is intentionally NOT a feature — it's governed by the
 * master `Client.enabled` switch, which is exactly the "is this client live at all" semantic.
 */
export type ClientFeatureKey =
  | 'scheduling' // Verizon partner-contact cron + trigger hook, Dell route-planning cron
  | 'replyCodeUpdater' // Dell reply-code & ETA updater cron
  | 'partPickup' // Dell outbound PUDO part pickup emails
  | 'partExchange'; // Dell part exchange outbound + inbound mail worker

/**
 * Which features apply to each `WorkflowType`. Single source of truth for both the server-side
 * gating checks and the per-client toggle UI (which renders one switch per entry here). The
 * `satisfies` clause forces every `WorkflowType` to be covered with valid keys — adding a new
 * workflow type without listing its features becomes a compile error.
 */
export const WORKFLOW_FEATURES = {
  [WorkflowType.DELLFRANCE]: ['scheduling', 'replyCodeUpdater', 'partPickup', 'partExchange'],
  [WorkflowType.VERIZON]: ['scheduling'],
  [WorkflowType.OSS]: [],
  [WorkflowType.PARKLAND]: [],
  [WorkflowType.UNKNOWN]: [],
} as const satisfies Record<WorkflowType, readonly ClientFeatureKey[]>;

/** Features available for a workflow type (empty for unknown/unmapped). */
export const getFeaturesForWorkflow = (workflowType: WorkflowType | null | undefined): readonly ClientFeatureKey[] =>
  WORKFLOW_FEATURES[workflowType ?? WorkflowType.UNKNOWN] ?? [];

/** Human-readable labels for the per-client feature toggles in the Nexus UI. */
export const FEATURE_LABELS = {
  scheduling: 'Scheduling',
  replyCodeUpdater: 'Reply-code & ETA updater',
  partPickup: 'Part pickups (PUDO)',
  partExchange: 'Part exchanges',
} as const satisfies Record<ClientFeatureKey, string>;
